import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  COCINA_VERSION_NO_DISPONIBLE,
  type ComandaCocinaVersionProps,
  invalidateComandasCocinaVersion,
  useComandasCocinaVersion,
} from '../../restaurante/api/apiComandaCocinaVersion.ts'
import {
  type ComandasCocinaProps,
  KEY_COMANDAS_COCINA,
  useComandasCocina,
} from '../../restaurante/api/apiComandasCocina.ts'
import type { ComandaProps } from '../../restaurante/interfaces/comanda.ts'

/** Intervalo por defecto de consulta de versión (Redis) */
export const COCINA_INTERVALO_MS = 3000

/** Con Redis caído (versión -1) los datos se recargan directamente cada este tiempo */
const COCINA_RECARGA_SIN_REDIS_MS = 15_000
export default COCINA_RECARGA_SIN_REDIS_MS

export interface UseComandasCocinaSyncParams {
  /** Sucursal de la sesión (0 es válido: casa matriz) */
  codigoSucursal: number
  /** _id de las áreas a observar; [] = todas. Debe ser una referencia estable (useMemo) */
  areas: string[]
  /** false para no consultar (ej. sin sucursal en la sesión) */
  enabled?: boolean
  /** Milisegundos entre consultas de versión. Por defecto 3000 */
  intervalo?: number
}

/**
 * Versión a comparar con la de la lista: la mayor de las áreas consultadas.
 * null si Redis no está disponible (alguna versión -1) o no hay datos: hay que recargar la lista.
 */
const versionMaxima = (versiones: ComandaCocinaVersionProps[]) => {
  if (versiones.length === 0 || versiones.some((v) => v.version === COCINA_VERSION_NO_DISPONIBLE)) return null
  return Math.max(...versiones.map((v) => v.version))
}

/**
 * Sincroniza las comandas abiertas de cocina con el backend:
 * 1. Carga inicial: `restCocinaComandas` trae las comandas y su `version` (ej. 2). La lista no se
 *    consulta periódicamente.
 * 2. Con la lista cargada, cada `intervalo` consulta `restCocinaVersion` (solo Redis, muy barato).
 * 3. Si la versión del backend es distinta a la de la lista (ej. 3), se hace refetch de la lista
 *    y se despliegan los cambios. Si Redis no está disponible (-1), se recarga la lista cada 15 s.
 * El backend es la fuente de verdad: no se guarda estado de cocina en el navegador.
 */
export const useComandasCocinaSync = ({
  codigoSucursal,
  areas,
  enabled = true,
  intervalo = COCINA_INTERVALO_MS,
}: UseComandasCocinaSyncParams) => {
  const activo = enabled

  // 1. Lista: carga inicial y refetch solo cuando cambia la versión
  const listaQuery = useComandasCocina(
    { codigoSucursal, areas },
    {
      enabled: activo,
      // Al cambiar de áreas se muestra la lista anterior hasta que llega la nueva
      placeholderData: keepPreviousData,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )
  const { refetch: refetchLista, isFetching: listaCargando, dataUpdatedAt: listaActualizada } = listaQuery
  const versionLista = listaQuery.isPlaceholderData ? undefined : listaQuery.data?.version
  const listaCargada = versionLista !== undefined

  // 2. Versión: solo cuando ya hay lista (y su versión) para comparar
  const versionQuery = useComandasCocinaVersion(
    { codigoSucursal, areas },
    {
      enabled: activo && listaCargada,
      refetchInterval: intervalo,
      refetchIntervalInBackground: true,
      staleTime: 0,
      retry: 1,
    },
  )
  const { data: versiones, dataUpdatedAt } = versionQuery

  // 3. Cada respuesta de versión se evalúa una sola vez; si difiere de la lista, refetch
  const atendidaRef = useRef(0)
  useEffect(() => {
    if (!versiones || !dataUpdatedAt || versionLista === undefined) return
    // Si la lista se está recargando, se evalúa al terminar (el efecto vuelve a correr)
    if (listaCargando || atendidaRef.current === dataUpdatedAt) return
    atendidaRef.current = dataUpdatedAt
    const actual = versionMaxima(versiones)
    if (actual === null) {
      // Redis caído: la versión no sirve para comparar, se recarga la lista cada 15 s
      if (Date.now() - listaActualizada >= COCINA_RECARGA_SIN_REDIS_MS) void refetchLista()
      return
    }
    // Si las versiones son diferentes entonces volvemos a cargar el listado de comandas
    if (actual !== versionLista) void refetchLista()
  }, [versiones, dataUpdatedAt, versionLista, listaCargando, listaActualizada, refetchLista])

  const comandas = useMemo<ComandaProps[]>(() => listaQuery.data?.comandas ?? [], [listaQuery.data])

  /** Recarga la lista ya (ej. tras un conflicto de estado o al pulsar "Actualizar") */
  const refrescar = useCallback(async () => {
    await refetchLista()
  }, [refetchLista])

  return {
    comandas,
    /** Versión de la lista mostrada */
    version: versionLista,
    /** true solo en la primera carga (sin datos todavía) */
    isLoading: activo && listaQuery.isLoading,
    isFetching: listaCargando,
    /** true mientras se muestra la lista anterior (cambio de áreas) */
    isPlaceholderData: listaQuery.isPlaceholderData,
    error: listaQuery.error ?? versionQuery.error,
    refrescar,
  }
}

/**
 * Reemplaza una comanda en todas las listas de cocina en caché con la versión que devolvió
 * el backend (respuesta de restCocinaCambiarEstado). Hace que la pantalla responda al instante;
 * en la siguiente consulta de versión, la lista se recarga y queda alineada con el backend.
 */
export const useActualizarComandaEnCache = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (comanda: ComandaProps) => {
      queryClient.setQueriesData<ComandasCocinaProps>({ queryKey: [KEY_COMANDAS_COCINA] }, (actual) =>
        actual
          ? { ...actual, comandas: actual.comandas.map((c) => (c._id === comanda._id ? comanda : c)) }
          : actual,
      )
      void invalidateComandasCocinaVersion()
    },
    [queryClient],
  )
}

/** true si el error es el conflicto de concurrencia de restCocinaCambiarEstado */
export const esConflictoEstadoCocina = (error: unknown) => {
  if (!(error instanceof Error)) return false
  return `${error.name} ${error.message}`.includes('COCINA_ESTADO_CONFLICTO')
}
